import { useMemo, useState } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { useBrowseState, useRememberedScroll } from '../../application/plants/browseState';
import { useGarden } from '../../application/plants/useGarden';
import { usePlantCatalogue } from '../../application/plants/usePlantCatalogue';
import { selectPlants } from '../../domain/plants/plantCatalogue';
import { NO_FILTERS, toggleFilterValue } from '../../domain/plants/plantFilters';
import { buildCategoryRails } from '../../domain/plants/plantRails';
import AmbientGarden from '../chrome/AmbientGarden';
import { LeafGlyph } from '../chrome/BrandIcons';
import CatalogueControls from '../plants/CatalogueControls';
import CategoryRail from '../plants/CategoryRail';
import FilterSheet from '../plants/FilterSheet';

/**
 * Browse the catalogue as shelves (FR-03, FR-04, SPEC-011, SPEC-012).
 *
 * One presentation, always: a horizontal shelf per category, including while
 * searching and filtering, with the emptied shelves dropped. SPEC-011 had a
 * second — a grid the page collapsed to when one category was chosen, reached by
 * a `View all` on each shelf — and SPEC-012 D-1 removes it. The screen no longer
 * has a mode, which is why there is no state here saying which one it is in.
 *
 * The query and the filters live in component state rather than the URL. The URL
 * is the source of truth for WHICH SCREEN and WHICH LOCALE (CON-002), and a
 * filter sheet that rewrites history on every checkbox turns the back button
 * into an undo stack. What it does not mean is losing them on the way to a plant
 * and back — that is `browseState`'s job.
 */
export default function PlantsScreen() {
    const { t, languageCode } = useLocale();
    const { status, plants } = usePlantCatalogue();
    const { isInGarden, toggle } = useGarden();
    const { query, setQuery, filters, setFilters } = useBrowseState();
    const [sheetOpen, setSheetOpen] = useState(false);

    const visible = useMemo(
        () => selectPlants(plants, { query, filters, languageCode }),
        [plants, query, filters, languageCode],
    );

    const rails = useMemo(
        () => buildCategoryRails(visible, { languageCode }),
        [visible, languageCode],
    );

    useRememberedScroll(status === 'ready');

    const toggleFilter = (groupKey, value) => setFilters(toggleFilterValue(filters, groupKey, value));

    return (
        <section className="plants-screen">
            {/* The ground this screen stands in (SPEC-023). First child so the
                document order matches the paint order; it is `position: fixed`
                and takes no space. */}
            <AmbientGarden />
            {/* The approved browse screen goes straight from the brand header to
                the search field — the title would cost a third of the fold and
                say nothing the search placeholder does not. It stays in the
                document because a screen still needs a heading to be announced
                and navigated by; only its pixels are gone. */}
            <div className="visually-hidden">
                <h1>{t('plants.title')}</h1>
                <p>{t('plants.subtitle')}</p>
            </div>

            <CatalogueControls
                query={query}
                onQueryChange={setQuery}
                filters={filters}
                onToggleFilter={toggleFilter}
                onClearFilters={() => setFilters(NO_FILTERS)}
                onOpenFilters={() => setSheetOpen(true)}
                resultCount={visible.length}
            />

            {status === 'loading' ? <p className="plants-screen__status">{t('plants.loading')}</p> : null}

            {status === 'failed' ? (
                <div className="empty-state">
                    <LeafGlyph className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('plants.failed')}</p>
                </div>
            ) : null}

            {status === 'ready' && visible.length === 0 ? (
                <div className="empty-state">
                    <LeafGlyph className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('plants.noResults')}</p>
                    <button type="button" className="button button--quiet" onClick={() => setFilters(NO_FILTERS)}>
                        {t('plants.filter.clear')}
                    </button>
                </div>
            ) : null}

            {rails.map((rail) => (
                <CategoryRail
                    key={rail.key}
                    rail={rail}
                    isSaved={isInGarden}
                    onToggleSave={toggle}
                />
            ))}

            {sheetOpen ? (
                <FilterSheet
                    plants={plants}
                    filters={filters}
                    onToggleFilter={toggleFilter}
                    onClear={() => setFilters(NO_FILTERS)}
                    onClose={() => setSheetOpen(false)}
                    resultCount={visible.length}
                />
            ) : null}
        </section>
    );
}
